import React from 'react';
import {
  Form,
  useActionData,
  useLoaderData,
  useLocation,
  useNavigate,
  useOutlet,
  useParams,
  useNavigation,
  useSubmit
} from 'react-router-dom';

import { Await } from '@oysterjs/uiv2/async';
import {
  CreateIntegrationInput,
  CreateIntegrationMutation,
  GetIntegrationDocument,
  GetIntegrationQuery,
  IntegrationDetailFieldsFragment,
  IntegrationFieldsFragment,
  IntegrationState,
  IntegrationSyncLogFieldsFragment,
  IntegrationSyncState,
  IntegrationType,
  HubSpotCallSyncType,
  HubSpotTaskSyncType,
  LoggedInAgencyUserFieldsFragment,
  SyncIntegrationDocument,
  SyncIntegrationMutation,
  useUpdateIntegrationMutation,
  useGetHubSpotTicketPipelinesQuery,
  useGetIntegrationStateQuery
} from '../../api/graphql';
import { Spinner } from '@oysterjs/uiv2/spinner';
import { AnimatePresence } from 'motion/react';
import { Button, PrimaryButton } from '@oysterjs/uiv2/button';
import { Badge } from '@oysterjs/uiv2/badge';
import clsx from 'clsx';
import { MountableSlideout } from '@oysterjs/uiv2/slideout';
import {
  Description,
  ErrorMessage,
  Field,
  FieldGroup,
  Fieldset,
  Label
} from '@oysterjs/uiv2/fieldset';
import { Switch, SwitchField } from '@oysterjs/uiv2/switch';
import { Input } from '@oysterjs/uiv2/input';
import { ValidationError } from '@oysterjs/corev2/graphql';
import { ActionResult } from '@oysterjs/corev2/graphql/actions';
import { useFormActions } from '@oysterjs/corev2/graphql/form';
import { client } from '../../api/client';
import { ApolloError } from '@apollo/client';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Combobox } from '@oysterjs/uiv2/combobox';

const titleCase = (s: string) =>
  s
    .split(/[\s_]/g)
    .map((part) => part[0].toUpperCase() + part.substring(1).toLowerCase())
    .join(' ');

const integrations = [
  {
    name: 'Hawksoft',
    type: IntegrationType.AmsHawksoft,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Policies'],
    icon: 'https://media.licdn.com/dms/image/v2/C4E0BAQFf1O-K5VVDTQ/company-logo_200_200/company-logo_200_200/0/1630652782314/hawksoft_inc_logo?e=2147483647&v=beta&t=vob2LV6WEJjB6IQ29IjOoRvJG1Hq3oSEVbCKa7ZTEh4'
  },
  {
    name: 'HubSpot',
    type: IntegrationType.CrmHubspot,
    category: 'CRM',
    comingSoon: false,
    tags: ['Clients', 'Tickets', 'Emails', 'Tasks', 'Calls'],
    icon: 'https://cdn4.iconfinder.com/data/icons/logos-and-brands/512/168_Hubspot_logo_logos-512.png'
  },
  {
    name: 'Zapier',
    category: 'Automation',
    comingSoon: true,
    tags: [],
    icon: 'https://cdn.freebiesupply.com/logos/large/2x/zapier-logo-svg-vector.svg'
  },
  {
    name: 'Applied Epic',
    category: 'AMS',
    comingSoon: true,
    tags: ['Clients', 'Policies'],
    icon: 'https://media-s3-us-east-1.ceros.com/applied-systems/images/2020/02/06/a6c5b28a3c58abd79f67017a848c1b8f/asset-9.png'
  },
  {
    name: 'AMS360',
    category: 'AMS',
    comingSoon: true,
    tags: ['Clients', 'Policies'],
    icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTa7wS-qfS1JyfoZ2Mbri8yMGh4HMeP52-LGw&s'
  },
  {
    name: 'NowCerts Momentum',
    type: IntegrationType.AmsNowcerts,
    category: 'AMS',
    comingSoon: false,
    tags: ['Clients', 'Policies'],
    icon: 'https://media.licdn.com/dms/image/v2/D560BAQFA33dds1mSpA/company-logo_200_200/company-logo_200_200/0/1731698561679/momentumamp_logo?e=2147483647&v=beta&t=GHkVoJy3eOXGQS1qJPJQ_oxJPIfQhxjqFqAq4JZP640'
  },
  {
    name: 'AgencyZoom',
    category: 'AMS',
    comingSoon: true,
    tags: ['Clients'],
    icon: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTa7wS-qfS1JyfoZ2Mbri8yMGh4HMeP52-LGw&s'
  },
  {
    name: 'Salesforce',
    category: 'CRM',
    comingSoon: true,
    tags: ['Clients', 'Tickets', 'Emails'],
    icon: 'https://upload.wikimedia.org/wikipedia/commons/thumb/f/f9/Salesforce.com_logo.svg/1200px-Salesforce.com_logo.svg.png'
  },
  {
    name: 'EZLynx',
    category: 'AMS',
    comingSoon: true,
    tags: ['Clients', 'Policies'],
    icon: 'https://media.licdn.com/dms/image/v2/D4E0BAQE5DofRfjrq4g/company-logo_200_200/company-logo_200_200/0/1705671105156/ezlynx_logo?e=2147483647&v=beta&t=006dAyH8Lrc3-b9kdvuYzVYkzjI0z32pBR9Uai6-v_s'
  }
];

export const SettingsIntegrationsPage = () => {
  const outlet = useOutlet();
  const loader = useLoaderData() as {
    data: Promise<{
      agencyUser: LoggedInAgencyUserFieldsFragment;
      integrations: IntegrationFieldsFragment[];
    }>;
  };

  return (
    <>
      <React.Suspense fallback={<IntegrationsPageLoader />}>
        <Await resolve={loader.data}>
          {(data) =>
            data?.agencyUser?.agency.subscription && (
              <div className="space-y-6">
                <IntegrationStatusBanner />
                <div className="divide-y divide-neutral-200 rounded-lg overflow-hidden bg-white shadow">
                  <div className="px-4 py-5 sm:px-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-medium m-0 p-0 text-neutral-900">Integrations</h3>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 gap-[1px] sm:grid-cols-2 xl:grid-cols-3 bg-neutral-200">
                    {integrations
                      .sort((a, b) => {
                        const aConnected = data?.integrations.find((i) => i.type === a.type);
                        const bConnected = data?.integrations.find((i) => i.type === b.type);
                        if (aConnected && !bConnected) {
                          return -1;
                        }
                        if (!aConnected && bConnected) {
                          return 1;
                        }

                        if (a.comingSoon && !b.comingSoon) {
                          return 1;
                        }
                        if (!a.comingSoon && b.comingSoon) {
                          return -1;
                        }
                        const categoryCompare = a.category.localeCompare(b.category);
                        if (categoryCompare !== 0) {
                          return categoryCompare;
                        }
                        return a.name.localeCompare(b.name);
                      })
                      .map((integration) => (
                        <div
                          key={integration.name}
                          className={clsx(
                            'px-4 py-5 sm:px-6 bg-white z-20',
                            integration.comingSoon && 'bg-neutral-200 opacity-80'
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <img
                                src={integration.icon}
                                alt={integration.name}
                                className="h-8 rounded-md"
                              />
                              <h4 className="font-medium m-0 p-0 text-neutral-900">
                                {integration.name}
                              </h4>
                            </div>
                            {!!data?.integrations.find((i) => i.type === integration.type) && (
                              <Badge color="green">Connected</Badge>
                            )}
                          </div>
                          <div className="flex flex-wrap gap-2 mt-4">
                            <Badge color="neutral">{integration.category}</Badge>
                            {integration.tags.map((tag) => (
                              <Badge key={tag} color="neutral">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                          <div className={clsx('mt-4', integration.comingSoon && 'opacity-70')}>
                            {integration.comingSoon && (
                              <Button
                                color={integration.comingSoon ? 'neutral' : 'sky'}
                                disabled={integration.comingSoon}
                                className="w-full data-[disabled]:cursor-not-allowed"
                              >
                                Coming Soon
                              </Button>
                            )}
                            {!integration.comingSoon &&
                              !data?.integrations.find((i) => i.type === integration.type) && (
                                <Button
                                  color="sky"
                                  className="w-full"
                                  href={`/app/settings/integrations/connect/${integration.type}`}
                                >
                                  Connect
                                </Button>
                              )}
                            {!integration.comingSoon &&
                              !!data?.integrations.find((i) => i.type === integration.type) && (
                                <div className="flex items-center gap-2">
                                  <Button
                                    color="white"
                                    className="w-full"
                                    href={`/app/settings/integrations/manage/${
                                      data.integrations.find((i) => i.type === integration.type)?.id
                                    }`}
                                  >
                                    Manage
                                  </Button>
                                  {/* <Button color="white" className="w-full">
                                    Disable
                                  </Button> */}
                                </div>
                              )}
                          </div>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            )
          }
        </Await>
      </React.Suspense>
      <AnimatePresence mode="wait">{outlet && React.cloneElement(outlet)}</AnimatePresence>
    </>
  );
};

const IntegrationsPageLoader = () => {
  return (
    <div className="px-6 rounded-lg bg-white shadow">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading settings...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

export const IntegrationStatusBanner = () => {
  return (
    <div className="overflow-hidden rounded-lg bg-gradient-to-r from-sky-700 to-sky-600">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium m-0 p-0 text-neutral-50">
            Get started with integrations
          </h3>
          <Button className="hidden" color="white">
            Upgrade
          </Button>
        </div>
        <p className="mt-2 text-sm text-neutral-200">
          Integrations enable Cara to automatically retrieve, index, and populate data from your
          existing systems, allowing her to work effectively in your agency's context. Cara can also
          be configured to write certain information back to your systems.
        </p>
      </div>
    </div>
  );
};

export const IntegrationConnectPage = () => {
  const { type } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const onClose = () => {
    if (location.key === 'default') {
      navigate('/app/settings/integrations');
    } else {
      navigate(-1);
    }
  };

  return (
    <MountableSlideout onClose={onClose} title="Connect Integration">
      {type === IntegrationType.AmsHawksoft && <ConnectHawksoft />}
      {type === IntegrationType.AmsNowcerts && <ConnectNowCerts />}
      {type === IntegrationType.CrmHubspot && <ConnectHubSpot />}
    </MountableSlideout>
  );
};

const ConnectHawksoft = () => {
  const navigation = useNavigation();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateIntegrationMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CreateIntegrationInput>({
    type: IntegrationType.AmsHawksoft,
    hawksoft: {
      agencyId: ''
    }
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(form, {
      method: 'POST',
      encType: 'application/json'
    });
  };

  return (
    <div>
      <h2 className="text-lg font-medium m-0 p-0 text-neutral-900">Hawksoft Integration</h2>
      <p className="mt-2 text-sm text-neutral-500">
        Connect your Hawksoft 6 AMS to Cara to automatically retrieve, index, and search data from
        your agency management system and write activity back.
      </p>
      <Form className="mt-4" onSubmit={onSubmit}>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Agency ID</Label>
              <Description>Enter your Hawksoft Agency ID.</Description>
              <Input
                placeholder="Agency ID"
                onChange={(e) => {
                  const agencyId = e.currentTarget.value;
                  updateForm(['hawksoft', 'agencyId'], (prev) => ({
                    ...prev,
                    hawksoft: prev.hawksoft
                      ? {
                          ...prev.hawksoft,
                          agencyId
                        }
                      : undefined
                  }));
                }}
                invalid={!!getValidationError(validationErrors, ['hawksoft', 'agencyId'])}
              />
              {!!getValidationError(validationErrors, ['hawksoft', 'agencyId']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['hawksoft', 'agencyId'])}
                </ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
        <div className="mt-6">
          {action?.applicationError && (
            <ErrorMessage>{action.applicationError.message}</ErrorMessage>
          )}
          <PrimaryButton
            className="w-full mt-2"
            type="submit"
            loading={navigation.state === 'submitting'}
          >
            Connect
          </PrimaryButton>
        </div>
      </Form>
    </div>
  );
};

const ConnectNowCerts = () => {
  const navigation = useNavigation();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateIntegrationMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CreateIntegrationInput>({
    type: IntegrationType.AmsNowcerts,
    nowcerts: {
      username: '',
      password: '',
      enableSyncTasks: false,
      enableSyncCalls: false
    }
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(form, {
      method: 'POST',
      encType: 'application/json'
    });
  };

  return (
    <div>
      <h2 className="text-lg font-medium m-0 p-0 text-neutral-900">NowCerts Integration</h2>
      <p className="mt-2 text-sm text-neutral-500">
        Connect your NowCerts AMS to Cara to automatically retrieve, index, and search data from
        your agency management system and write activity back.
      </p>
      <Form className="mt-4" onSubmit={onSubmit}>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Username</Label>
              <Description>Enter your NowCerts username.</Description>
              <Input
                placeholder="Username"
                onChange={(e) => {
                  const username = e.currentTarget.value;
                  updateForm(['nowcerts', 'username'], (prev) => ({
                    ...prev,
                    nowcerts: prev.nowcerts
                      ? {
                          ...prev.nowcerts,
                          username
                        }
                      : undefined
                  }));
                }}
                invalid={!!getValidationError(validationErrors, ['nowcerts', 'username'])}
              />
              {!!getValidationError(validationErrors, ['nowcerts', 'username']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['nowcerts', 'username'])}
                </ErrorMessage>
              )}
            </Field>
            <Field>
              <Label>Password</Label>
              <Description>Enter your NowCerts password.</Description>
              <Input
                placeholder="Password"
                type="password"
                onChange={(e) => {
                  const password = e.currentTarget.value;
                  updateForm(['nowcerts', 'password'], (prev) => ({
                    ...prev,
                    nowcerts: prev.nowcerts
                      ? {
                          ...prev.nowcerts,
                          password
                        }
                      : undefined
                  }));
                }}
                invalid={!!getValidationError(validationErrors, ['nowcerts', 'password'])}
              />
              {!!getValidationError(validationErrors, ['nowcerts', 'password']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['nowcerts', 'password'])}
                </ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>

        <div className="mt-6">
          {action?.applicationError && (
            <ErrorMessage>
              {action.applicationError.message.includes('authentication failed') ||
              action.applicationError.message.includes('invalid_grant')
                ? 'Invalid credentials'
                : action.applicationError.message}
            </ErrorMessage>
          )}
          <PrimaryButton
            className="w-full mt-2"
            type="submit"
            loading={navigation.state === 'submitting'}
          >
            Connect
          </PrimaryButton>
        </div>
      </Form>
    </div>
  );
};

const ConnectHubSpot = () => {
  const navigation = useNavigation();
  const submit = useSubmit();
  const action = useActionData() as ActionResult<CreateIntegrationMutation>;

  const [validationErrors, setValidationErrors] = React.useState<ValidationError[]>([]);
  const [form, setForm] = React.useState<CreateIntegrationInput>({
    type: IntegrationType.CrmHubspot,
    hubspot: {
      accessToken: ''
    }
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  React.useEffect(() => {
    if (action?.validationErrors) {
      setValidationErrors(action.validationErrors);
    }
  }, [action?.validationErrors]);

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    submit(form, {
      method: 'POST',
      encType: 'application/json'
    });
  };

  return (
    <div>
      <h2 className="text-lg font-medium m-0 p-0 text-neutral-900">HubSpot Integration</h2>
      <p className="mt-2 text-sm text-neutral-500">
        Connect your HubSpot CRM to Cara to automatically sync tasks, calls, and other activities.
      </p>
      <Form className="mt-4" onSubmit={onSubmit}>
        <Fieldset>
          <FieldGroup>
            <Field>
              <Label>Personal Access Token</Label>
              <Description>
                Enter your HubSpot Personal Access Token. You can create one in your HubSpot
                developer settings.
              </Description>
              <Input
                placeholder="pat-na1-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                type="password"
                onChange={(e) => {
                  const accessToken = e.currentTarget.value;
                  updateForm(['hubspot', 'accessToken'], (prev) => ({
                    ...prev,
                    hubspot: {
                      accessToken
                    }
                  }));
                }}
                invalid={!!getValidationError(validationErrors, ['hubspot', 'accessToken'])}
              />
              {!!getValidationError(validationErrors, ['hubspot', 'accessToken']) && (
                <ErrorMessage>
                  {getValidationError(validationErrors, ['hubspot', 'accessToken'])}
                </ErrorMessage>
              )}
            </Field>
          </FieldGroup>
        </Fieldset>
        <div className="mt-6">
          {action?.applicationError && (
            <ErrorMessage>
              {action.applicationError.message.includes('authentication failed') ||
              action.applicationError.message.includes('invalid_grant')
                ? 'Invalid access token'
                : action.applicationError.message}
            </ErrorMessage>
          )}
          <PrimaryButton
            className="w-full mt-2"
            type="submit"
            loading={navigation.state === 'submitting'}
          >
            Connect
          </PrimaryButton>
        </div>
      </Form>
    </div>
  );
};

export const IntegrationManagePage = () => {
  const loader = useLoaderData() as {
    data: Promise<{
      integration: IntegrationDetailFieldsFragment;
    }>;
  };
  const navigate = useNavigate();

  const onClose = () => {
    navigate('/app/settings/integrations');
  };

  return (
    <MountableSlideout onClose={onClose} title="Manage Integration" size="xl">
      <React.Suspense fallback={<IntegrationsManagePageLoader />}>
        <Await resolve={loader?.data}>
          {(data) =>
            data?.integration.type === IntegrationType.AmsHawksoft ? (
              <ManageHawksoft integration={data.integration} />
            ) : data?.integration.type === IntegrationType.AmsNowcerts ? (
              <ManageNowCerts integration={data.integration} />
            ) : data?.integration.type === IntegrationType.CrmHubspot ? (
              <ManageHubSpot integration={data.integration} />
            ) : null
          }
        </Await>
      </React.Suspense>
    </MountableSlideout>
  );
};

const IntegrationsManagePageLoader = () => {
  return (
    <div className="px-6">
      <div className="text-center py-12">
        <Spinner size="size-8" color="neutral" />
        <h3 className="mt-2 text-sm font-semibold text-neutral-900">Loading integration...</h3>
        <p className="mt-1 text-sm text-neutral-500">This might take a moment.</p>
      </div>
    </div>
  );
};

const ManageHawksoft = ({ integration }: { integration: IntegrationDetailFieldsFragment }) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [loadingCalls, setLoadingCalls] = React.useState(false);
  const [enableSyncTasks, setEnableSyncTasks] = React.useState(
    integration.configuration?.hawksoft?.enableSyncServiceRequests || false
  );
  const [enableSyncCalls, setEnableSyncCalls] = React.useState(
    integration.configuration?.hawksoft?.enableSyncCallNotes || false
  );
  const [showSavedTasks, setShowSavedTasks] = React.useState(false);
  const [showSavedCalls, setShowSavedCalls] = React.useState(false);

  // Update local state when integration prop changes
  React.useEffect(() => {
    setEnableSyncTasks(integration.configuration?.hawksoft?.enableSyncServiceRequests || false);
    setEnableSyncCalls(integration.configuration?.hawksoft?.enableSyncCallNotes || false);
  }, [
    integration.configuration?.hawksoft?.enableSyncServiceRequests,
    integration.configuration?.hawksoft?.enableSyncCallNotes
  ]);

  // Unified handler for sync toggles
  const handleSyncToggle = async (
    type: 'enableSyncServiceRequests' | 'enableSyncCallNotes',
    enabled: boolean
  ) => {
    if (type === 'enableSyncServiceRequests') setEnableSyncTasks(enabled);
    if (type === 'enableSyncCallNotes') setEnableSyncCalls(enabled);

    if (type === 'enableSyncServiceRequests') setLoadingTasks(true);
    if (type === 'enableSyncCallNotes') setLoadingCalls(true);

    try {
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            hawksoft: {
              [type]: enabled
            }
          }
        }
      });
      if (type === 'enableSyncServiceRequests') {
        setShowSavedTasks(true);
        setTimeout(() => setShowSavedTasks(false), 2000);
      } else if (type === 'enableSyncCallNotes') {
        setShowSavedCalls(true);
        setTimeout(() => setShowSavedCalls(false), 2000);
      }
    } catch (error) {
      if (type === 'enableSyncServiceRequests') setEnableSyncTasks(!enabled);
      if (type === 'enableSyncCallNotes') setEnableSyncCalls(!enabled);
    } finally {
      if (type === 'enableSyncServiceRequests') setLoadingTasks(false);
      if (type === 'enableSyncCallNotes') setLoadingCalls(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900">Hawksoft Integration</h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500">
        Connect your Hawksoft 6 AMS to Cara to automatically retrieve, index, and search data from
        your agency management system and write activity back.
      </p>
      <dl className="mt-4">
        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900">Agency ID</dt>
            <dd className="text-sm/6 text-neutral-700">
              {integration.configuration?.hawksoft?.agencyId}
            </dd>
          </div>
        </div>
      </dl>
      <div className="mt-4 space-y-4">
        <h3 className="text-sm font-medium text-neutral-900">Sync Settings</h3>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Enable Sync Tasks</Label>
            {showSavedTasks && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500" />
                <p className="text-xs/6 text-neutral-500">Saved</p>
              </div>
            )}
          </div>
          <Description>Sync service requests from Cara to Hawksoft.</Description>
          <Switch
            checked={enableSyncTasks}
            onChange={(enabled) => handleSyncToggle('enableSyncServiceRequests', enabled)}
            disabled={loadingTasks}
            color="sky"
          />
        </SwitchField>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Enable Sync Calls</Label>
            {showSavedCalls && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500" />
                <p className="text-xs/6 text-neutral-500">Saved</p>
              </div>
            )}
          </div>
          <Description>Sync call notes from Cara to Hawksoft.</Description>
          <Switch
            checked={enableSyncCalls}
            onChange={(enabled) => handleSyncToggle('enableSyncCallNotes', enabled)}
            disabled={loadingCalls}
            color="sky"
          />
        </SwitchField>
      </div>

      <div className="mt-4">
        <IntegrationSyncLogs integration={integration} />
      </div>
    </div>
  );
};

const ManageNowCerts = ({ integration }: { integration: IntegrationDetailFieldsFragment }) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [loadingCalls, setLoadingCalls] = React.useState(false);
  const [enableSyncTasks, setEnableSyncTasks] = React.useState(
    integration.configuration?.nowcerts?.enableSyncTasks || false
  );
  const [enableSyncCalls, setEnableSyncCalls] = React.useState(
    integration.configuration?.nowcerts?.enableSyncCalls || false
  );
  const [showSavedTasks, setShowSavedTasks] = React.useState(false);
  const [showSavedCalls, setShowSavedCalls] = React.useState(false);

  // Update local state when integration prop changes
  React.useEffect(() => {
    setEnableSyncTasks(integration.configuration?.nowcerts?.enableSyncTasks || false);
    setEnableSyncCalls(integration.configuration?.nowcerts?.enableSyncCalls || false);
  }, [
    integration.configuration?.nowcerts?.enableSyncTasks,
    integration.configuration?.nowcerts?.enableSyncCalls
  ]);

  // Unified handler for sync toggles
  const handleSyncToggle = async (
    type: 'enableSyncTasks' | 'enableSyncCalls',
    enabled: boolean
  ) => {
    if (type === 'enableSyncTasks') setEnableSyncTasks(enabled);
    if (type === 'enableSyncCalls') setEnableSyncCalls(enabled);

    if (type === 'enableSyncTasks') setLoadingTasks(true);
    if (type === 'enableSyncCalls') setLoadingCalls(true);

    try {
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            nowcerts: {
              [type]: enabled
            }
          }
        }
      });
      if (type === 'enableSyncTasks') {
        setShowSavedTasks(true);
        setTimeout(() => setShowSavedTasks(false), 2000);
      } else if (type === 'enableSyncCalls') {
        setShowSavedCalls(true);
        setTimeout(() => setShowSavedCalls(false), 2000);
      }
    } catch (error) {
      if (type === 'enableSyncTasks') setEnableSyncTasks(!enabled);
      if (type === 'enableSyncCalls') setEnableSyncCalls(!enabled);
    } finally {
      if (type === 'enableSyncTasks') setLoadingTasks(false);
      if (type === 'enableSyncCalls') setLoadingCalls(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900">NowCerts Integration</h2>
        <Badge
          color={
            integration.state === IntegrationState.Active
              ? 'green'
              : integration.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {integration.state}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500">
        Connect your NowCerts AMS to Cara to automatically retrieve, index, and search data from
        your agency management system and write activity back.
      </p>
      <dl className="mt-4">
        <div className="flex flex-col sm:gap-1.5 gap-3 pb-6">
          <div className="flex flex-col gap-0.5">
            <dt className="text-sm/6 font-medium text-neutral-900">Username</dt>
            <dd className="text-sm/6 text-neutral-700">
              {integration.configuration?.nowcerts?.username || 'Not configured'}
            </dd>
          </div>
        </div>
      </dl>

      <div className="mt-4 space-y-4">
        <h3 className="text-sm font-medium text-neutral-900">Sync Settings</h3>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Enable Sync Tasks</Label>
            {showSavedTasks && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500" />
                <p className="text-xs/6 text-neutral-500">Saved</p>
              </div>
            )}
          </div>
          <Description>Sync tasks from Cara to NowCerts.</Description>
          <Switch
            checked={enableSyncTasks}
            onChange={(enabled) => handleSyncToggle('enableSyncTasks', enabled)}
            disabled={loadingTasks}
            color="sky"
          />
        </SwitchField>
        <SwitchField>
          <div className="flex items-center gap-2">
            <Label>Enable Sync Calls</Label>
            {showSavedCalls && (
              <div className="flex items-center gap-1.5">
                <CheckIcon className="size-3 text-neutral-500" />
                <p className="text-xs/6 text-neutral-500">Saved</p>
              </div>
            )}
          </div>
          <Description>Sync calls from Cara to NowCerts.</Description>
          <Switch
            checked={enableSyncCalls}
            onChange={(enabled) => handleSyncToggle('enableSyncCalls', enabled)}
            disabled={loadingCalls}
            color="sky"
          />
        </SwitchField>
      </div>

      <div className="mt-4">
        <IntegrationSyncLogs integration={integration} />
      </div>
    </div>
  );
};

const ManageHubSpot = ({ integration }: { integration: IntegrationDetailFieldsFragment }) => {
  const [updateIntegration] = useUpdateIntegrationMutation();
  const [loadingTasks, setLoadingTasks] = React.useState(false);
  const [loadingCalls, setLoadingCalls] = React.useState(false);
  const [showSavedTasks, setShowSavedTasks] = React.useState(false);
  const [showSavedCalls, setShowSavedCalls] = React.useState(false);

  // Add query for integration state
  const { data: integrationData, refetch: refetchIntegration } = useGetIntegrationStateQuery({
    variables: { id: integration.id }
  });

  const [taskConfig, setTaskConfig] = React.useState({
    enabled: integration.configuration?.hubspot?.enableSyncTasks || false,
    syncType:
      integration.configuration?.hubspot?.taskSyncType || HubSpotTaskSyncType.EngagementTask,
    pipelineId: integration.configuration?.hubspot?.taskPipelineId || '',
    stageId: integration.configuration?.hubspot?.taskStageId || ''
  });

  const [callConfig, setCallConfig] = React.useState({
    enabled: integration.configuration?.hubspot?.enableSyncCalls || false,
    syncType:
      integration.configuration?.hubspot?.callSyncType || HubSpotCallSyncType.EngagementCall,
    pipelineId: integration.configuration?.hubspot?.callPipelineId || '',
    stageId: integration.configuration?.hubspot?.callStageId || ''
  });

  // Update local state when integration prop changes
  React.useEffect(() => {
    setTaskConfig({
      enabled: integration.configuration?.hubspot?.enableSyncTasks || false,
      syncType:
        integration.configuration?.hubspot?.taskSyncType || HubSpotTaskSyncType.EngagementTask,
      pipelineId: integration.configuration?.hubspot?.taskPipelineId || '',
      stageId: integration.configuration?.hubspot?.taskStageId || ''
    });
    setCallConfig({
      enabled: integration.configuration?.hubspot?.enableSyncCalls || false,
      syncType:
        integration.configuration?.hubspot?.callSyncType || HubSpotCallSyncType.EngagementCall,
      pipelineId: integration.configuration?.hubspot?.callPipelineId || '',
      stageId: integration.configuration?.hubspot?.callStageId || ''
    });
  }, [integration.configuration?.hubspot]);

  // Create a version of the integration with the latest state
  const currentIntegration = {
    ...integration,
    state: integrationData?.integration?.state || integration.state
  };

  const handleTaskConfigUpdate = async (updates: Partial<typeof taskConfig>) => {
    setLoadingTasks(true);
    const newConfig = { ...taskConfig, ...updates };
    setTaskConfig(newConfig);

    try {
      // Update HubSpot config - let API handle state transitions
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            hubspot: {
              enableSyncTasks: newConfig.enabled,
              taskSyncType: newConfig.enabled ? newConfig.syncType : undefined,
              taskPipelineId:
                newConfig.enabled && newConfig.syncType === HubSpotTaskSyncType.Ticket
                  ? newConfig.pipelineId
                  : undefined,
              taskStageId:
                newConfig.enabled && newConfig.syncType === HubSpotTaskSyncType.Ticket
                  ? newConfig.stageId
                  : undefined
            }
          }
        }
      });

      // Refetch integration state to ensure UI is in sync with database
      await refetchIntegration();

      setShowSavedTasks(true);
      setTimeout(() => setShowSavedTasks(false), 2000);
    } catch (error) {
      setTaskConfig(taskConfig); // Revert on error
      // Also refetch integration state in case of error
      await refetchIntegration();
    } finally {
      setLoadingTasks(false);
    }
  };

  const handleCallConfigUpdate = async (updates: Partial<typeof callConfig>) => {
    setLoadingCalls(true);
    const newConfig = { ...callConfig, ...updates };
    setCallConfig(newConfig);

    try {
      // First update the HubSpot config with default field mappings
      const defaultCallFieldMappings =
        newConfig.enabled && newConfig.syncType === HubSpotCallSyncType.Ticket
          ? {
              ticketFields: [
                { hubspotField: 'content', caraField: 'summary' },
                { hubspotField: 'content', caraField: 'transcription' },
                { hubspotField: 'subject', caraField: 'callFromName' }
              ],
              dealFields: [] // Required by type but not used for ticket sync
            }
          : undefined;

      // Update HubSpot config - let API handle state transitions
      await updateIntegration({
        variables: {
          id: integration.id,
          input: {
            hubspot: {
              enableSyncCalls: newConfig.enabled,
              callSyncType: newConfig.enabled ? newConfig.syncType : undefined,
              callPipelineId:
                newConfig.enabled && newConfig.syncType === HubSpotCallSyncType.Ticket
                  ? newConfig.pipelineId
                  : undefined,
              callStageId:
                newConfig.enabled && newConfig.syncType === HubSpotCallSyncType.Ticket
                  ? newConfig.stageId
                  : undefined,
              callFieldMappings: defaultCallFieldMappings
            }
          }
        }
      });

      // Refetch integration state to ensure UI is in sync with database
      await refetchIntegration();

      setShowSavedCalls(true);
      setTimeout(() => setShowSavedCalls(false), 2000);
    } catch (error) {
      setCallConfig(callConfig); // Revert on error
      // Also refetch integration state in case of error
      await refetchIntegration();
    } finally {
      setLoadingCalls(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-medium m-0 p-0 text-neutral-900">HubSpot Integration</h2>
        <Badge
          color={
            integrationData?.integration?.state === IntegrationState.Active
              ? 'green'
              : integrationData?.integration?.state === IntegrationState.Pending
                ? 'amber'
                : 'neutral'
          }
        >
          {titleCase(integrationData?.integration?.state || integration.state)}
        </Badge>
      </div>
      <p className="mt-2 text-sm text-neutral-500">
        Configure how Cara syncs tasks and calls with your HubSpot CRM.
      </p>

      <div className="mt-6 space-y-6">
        {/* Task Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-neutral-900">Task Sync Settings</h3>
          <SwitchField>
            <div className="flex items-center gap-2">
              <Label>Enable Task Sync</Label>
              {showSavedTasks && (
                <div className="flex items-center gap-1.5">
                  <CheckIcon className="size-3 text-neutral-500" />
                  <p className="text-xs/6 text-neutral-500">Saved</p>
                </div>
              )}
            </div>
            <Description>Sync tasks from Cara to HubSpot.</Description>
            <Switch
              checked={taskConfig.enabled}
              onChange={(enabled) => handleTaskConfigUpdate({ enabled })}
              disabled={loadingTasks}
              color="sky"
            />
          </SwitchField>

          {taskConfig.enabled && (
            <div className="space-y-4 pl-4 border-l-2 border-neutral-100">
              <Field>
                <Label>Sync Type</Label>
                <Description>Choose how tasks should be created in HubSpot.</Description>
                <select
                  className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-neutral-900 ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6"
                  value={taskConfig.syncType}
                  onChange={(e) =>
                    handleTaskConfigUpdate({ syncType: e.target.value as HubSpotTaskSyncType })
                  }
                  disabled={loadingTasks}
                >
                  <option value={HubSpotTaskSyncType.EngagementTask}>Task Engagement</option>
                  <option value={HubSpotTaskSyncType.Ticket}>Ticket</option>
                </select>
              </Field>

              {taskConfig.syncType === HubSpotTaskSyncType.Ticket && (
                <>
                  <Field>
                    <Label>Ticket Pipeline</Label>
                    <Description>Select the pipeline to create tickets in.</Description>
                    <React.Suspense fallback={<Spinner size="size-4" color="neutral" />}>
                      <HubSpotTicketPipelineSelect
                        integrationId={integration.id}
                        value={taskConfig.pipelineId}
                        onChange={(pipelineId) =>
                          handleTaskConfigUpdate({ pipelineId, stageId: '' })
                        }
                        disabled={loadingTasks}
                      />
                    </React.Suspense>
                  </Field>
                  {taskConfig.pipelineId && (
                    <Field>
                      <Label>Ticket Stage</Label>
                      <Description>Select the stage to create tickets in.</Description>
                      <React.Suspense fallback={<Spinner size="size-4" color="neutral" />}>
                        <HubSpotTicketStageSelect
                          integrationId={integration.id}
                          pipelineId={taskConfig.pipelineId}
                          value={taskConfig.stageId}
                          onChange={(stageId) => handleTaskConfigUpdate({ stageId })}
                          disabled={loadingTasks}
                        />
                      </React.Suspense>
                    </Field>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Call Configuration */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-neutral-900">Call Sync Settings</h3>
          <SwitchField>
            <div className="flex items-center gap-2">
              <Label>Enable Call Sync</Label>
              {showSavedCalls && (
                <div className="flex items-center gap-1.5">
                  <CheckIcon className="size-3 text-neutral-500" />
                  <p className="text-xs/6 text-neutral-500">Saved</p>
                </div>
              )}
            </div>
            <Description>Sync calls from Cara to HubSpot.</Description>
            <Switch
              checked={callConfig.enabled}
              onChange={(enabled) => handleCallConfigUpdate({ enabled })}
              disabled={loadingCalls}
              color="sky"
            />
          </SwitchField>

          {callConfig.enabled && (
            <div className="space-y-4 pl-4 border-l-2 border-neutral-100">
              <Field>
                <Label>Sync Type</Label>
                <Description>Choose how calls should be created in HubSpot.</Description>
                <select
                  className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-neutral-900 ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6"
                  value={callConfig.syncType}
                  onChange={(e) =>
                    handleCallConfigUpdate({ syncType: e.target.value as HubSpotCallSyncType })
                  }
                  disabled={loadingCalls}
                >
                  <option value={HubSpotCallSyncType.EngagementCall}>Call Engagement</option>
                  <option value={HubSpotCallSyncType.EngagementNote}>Note Engagement</option>
                  <option value={HubSpotCallSyncType.Ticket}>Ticket</option>
                </select>
              </Field>

              {callConfig.syncType === HubSpotCallSyncType.Ticket && (
                <>
                  <Field>
                    <Label>Ticket Pipeline</Label>
                    <Description>Select the pipeline to create tickets in.</Description>
                    <React.Suspense fallback={<Spinner size="size-4" color="neutral" />}>
                      <HubSpotTicketPipelineSelect
                        integrationId={integration.id}
                        value={callConfig.pipelineId}
                        onChange={(pipelineId) =>
                          handleCallConfigUpdate({ pipelineId, stageId: '' })
                        }
                        disabled={loadingCalls}
                      />
                    </React.Suspense>
                  </Field>
                  {callConfig.pipelineId && (
                    <Field>
                      <Label>Ticket Stage</Label>
                      <Description>Select the stage to create tickets in.</Description>
                      <React.Suspense fallback={<Spinner size="size-4" color="neutral" />}>
                        <HubSpotTicketStageSelect
                          integrationId={integration.id}
                          pipelineId={callConfig.pipelineId}
                          value={callConfig.stageId}
                          onChange={(stageId) => handleCallConfigUpdate({ stageId })}
                          disabled={loadingCalls}
                        />
                      </React.Suspense>
                    </Field>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <IntegrationSyncLogs integration={currentIntegration} />
      </div>
    </div>
  );
};

const InProgressIndicator = () => (
  <div className="relative size-2.5">
    <svg
      viewBox="0 0 10 10"
      aria-hidden="true"
      className="size-2.5 absolute top-0 left-0 z-0 animate-ping"
    >
      <circle r={5} cx={5} cy={5} className="fill-blue-300 opacity-95" />
    </svg>
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0">
      <circle r={4} cx={5} cy={5} className="fill-blue-400" />
    </svg>
  </div>
);

const CompletedIndicator = () => (
  <div className="relative size-2.5">
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0 z-0">
      <circle r={5} cx={5} cy={5} className="fill-green-300" />
    </svg>
  </div>
);

const FailedIndicator = () => (
  <div className="relative size-2.5">
    <svg viewBox="0 0 10 10" aria-hidden="true" className="size-2.5 absolute top-0 left-0 z-0">
      <circle r={5} cx={5} cy={5} className="fill-red-300" />
    </svg>
  </div>
);

const relativeDate = (now: Date, date: Date) => {
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  const diffInSeconds = Math.floor((date.getTime() - now.getTime()) / 1000);

  // Put the absolute date if the date is more than 7 days ago
  if (Math.abs(diffInSeconds) >= 604800) {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(date);
  }

  // Otherwise, show the relative date
  const units = [
    { unit: 'day', seconds: 86400 },
    { unit: 'hour', seconds: 3600 },
    { unit: 'minute', seconds: 60 },
    { unit: 'second', seconds: 1 }
  ];

  for (const { unit, seconds } of units) {
    if (Math.abs(diffInSeconds) >= seconds) {
      return rtf.format(Math.round(diffInSeconds / seconds), unit as Intl.RelativeTimeFormatUnit);
    }
  }

  return rtf.format(0, 'second');
};

interface IntegrationSyncLogsProps {
  integration: IntegrationDetailFieldsFragment;
}

const IntegrationSyncLogs = ({ integration }: IntegrationSyncLogsProps) => {
  const [loading, setLoading] = React.useState(false);
  const [logs, setLogs] = React.useState<IntegrationSyncLogFieldsFragment[]>(integration.syncLogs);
  const [lastSyncLogId, setLastSyncLogId] = React.useState<string>(logs?.[0]?.id);
  const [now, setNow] = React.useState(new Date());

  const syncIntegration = async () => {
    setLoading(true);

    const res = await client.mutate<SyncIntegrationMutation>({
      mutation: SyncIntegrationDocument,
      variables: {
        id: integration.id
      }
    });

    if (res.errors?.length) {
      throw new ApolloError({ graphQLErrors: res.errors });
    }

    const id = res.data?.syncIntegration?.id;
    if (!id) {
      throw new Error('Failed to sync integration');
    }

    setLogs(res.data?.syncIntegration?.syncLogs || []);
    setNow(new Date());
  };

  const getIntegration = async () => {
    const res = await client.query<GetIntegrationQuery>({
      query: GetIntegrationDocument,
      variables: { id: integration.id }
    });

    if (res.errors?.length) {
      throw new ApolloError({ graphQLErrors: res.errors });
    }

    setLogs(res.data?.integration?.syncLogs || []);
    setNow(new Date());
  };

  // Update logs when the integration is updated
  React.useEffect(() => {
    setLogs(integration.syncLogs);
  }, [integration.syncLogs]);

  // Set loading state to false when the last sync log id changes
  React.useEffect(() => {
    setLoading(loading && logs?.[0]?.id === lastSyncLogId);
    setLastSyncLogId(logs?.[0]?.id);
    setNow(new Date());
  }, [loading, logs?.[0]?.id, lastSyncLogId]);

  // Poll integration for updates while the most recent sync log is in progress
  React.useEffect(() => {
    if (loading || logs?.[0]?.state === IntegrationSyncState.InProgress) {
      const interval = setInterval(() => getIntegration(), 1000);
      return () => clearInterval(interval);
    }
  }, [loading, logs?.[0]?.id, logs?.[0]?.state, lastSyncLogId]);

  // Update the current time every 5 seconds
  React.useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {logs.length > 0 && (
        <div className="flex flex-col gap-2 overflow-auto border border-neutral-200 rounded-lg">
          <table className="min-w-full divide-y divide-neutral-200">
            <thead className="bg-neutral-50">
              <tr>
                <th
                  scope="col"
                  className="py-2 pr-3 pl-4 text-left text-sm font-semibold text-neutral-900"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-3 py-2 text-left text-sm font-semibold text-neutral-900"
                >
                  Result
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-200 bg-white">
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="py-4 pr-3 pl-4 text-sm whitespace-nowrap text-neutral-900">
                    <div className="font-medium flex items-center gap-2">
                      {log.state === IntegrationSyncState.Completed && <CompletedIndicator />}
                      {log.state === IntegrationSyncState.InProgress && <InProgressIndicator />}
                      {log.state === IntegrationSyncState.Failed && <FailedIndicator />}
                      {titleCase(log.type)}
                    </div>
                    <div className="text-neutral-500 text-xs mt-1 ml-[1.125rem]">
                      {relativeDate(now, new Date(log.startedAt))}
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm whitespace-nowrap text-neutral-500">
                    {log.lastError && (
                      <div className="text-red-600 text-xs whitespace-break-spaces">
                        {log.lastError}
                      </div>
                    )}
                    {!log.lastError && log.state === IntegrationSyncState.Completed && (
                      <div className="text-neutral-600 text-xs whitespace-break-spaces">
                        Successfully synced {log.entitiesSynced} entities
                      </div>
                    )}
                    {!log.lastError && log.state === IntegrationSyncState.InProgress && (
                      <div className="text-neutral-600 text-xs whitespace-break-spaces">
                        Syncing in progress...
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4">
        <PrimaryButton color="white" className="w-full" onClick={syncIntegration} loading={loading}>
          {integration.type === IntegrationType.CrmHubspot ? 'Save & Sync' : 'Sync Now'}
        </PrimaryButton>
      </div>
    </div>
  );
};

const HubSpotTicketPipelineSelect = ({
  integrationId,
  value,
  onChange,
  disabled
}: {
  integrationId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  const { data, loading, error } = useGetHubSpotTicketPipelinesQuery({
    variables: { integrationId }
  });

  if (loading) return <Spinner size="size-4" color="neutral" />;
  if (error) return null;

  const pipelines = data?.hubspotTicketPipelines || [];
  const activePipelines = pipelines.filter((p) => !p.archived);
  const selectedPipeline = activePipelines.find((p) => p.id === value);

  return (
    <Combobox
      value={selectedPipeline?.label || ''}
      minQueryLength={0}
      maxOptions={1000}
      onChange={(label) => {
        const pipeline = activePipelines.find((p) => p.label === label);
        if (pipeline) {
          onChange(pipeline.id);
        } else {
          // Handle empty selection
          onChange('');
        }
      }}
      options={[
        { value: '', label: 'Select a pipeline...', description: '' },
        ...activePipelines
          .sort((a, b) => a.displayOrder - b.displayOrder)
          .map((pipeline) => ({
            value: pipeline.label,
            label: pipeline.label,
            description: `${pipeline.stages.length} stages`
          }))
      ]}
      disabled={disabled}
    />
  );
};

const HubSpotTicketStageSelect = ({
  integrationId,
  pipelineId,
  value,
  onChange,
  disabled
}: {
  integrationId: string;
  pipelineId: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) => {
  const { data, loading, error } = useGetHubSpotTicketPipelinesQuery({
    variables: { integrationId }
  });

  if (loading) return <Spinner size="size-4" color="neutral" />;
  if (error) return null;

  const pipeline = data?.hubspotTicketPipelines?.find((p) => !p.archived && p.id === pipelineId);
  if (!pipeline) return null;

  const stages = [...(pipeline.stages || [])]; // Create a copy of the array

  return (
    <select
      className="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-neutral-900 ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-sky-600 sm:text-sm sm:leading-6"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    >
      <option value="">Select a stage...</option>
      {stages
        .sort((a, b) => a.displayOrder - b.displayOrder)
        .map((stage) => (
          <option key={stage.id} value={stage.id}>
            {stage.label}
          </option>
        ))}
    </select>
  );
};
